import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, LayoutGrid, Bookmark, Save, X } from 'lucide-react';

const BADGE_COLORS = {
  WO: 'bg-sky-100 text-sky-800 border-sky-200',
  SO: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  PO: 'bg-slate-200 text-slate-700 border-slate-300',
  PS: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  BOL: 'bg-amber-100 text-amber-800 border-amber-200',
  RO: 'bg-rose-100 text-rose-800 border-rose-200',
};

/**
 * OrdersOverviewPage — a single search bar across:
 *   Work Orders (v2) · Sales Orders · Production Orders · Packing Slips · BoL · Repair Orders
 * One pane of glass; backend fans out via /api/orders-search.
 */
const OrdersOverviewPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [docFilter, setDocFilter] = useState('__all__');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // iter106 — saved views
  const [views, setViews] = useState([]);
  const [newViewName, setNewViewName] = useState('');
  const [showSave, setShowSave] = useState(false);

  const loadViews = async () => {
    try { const r = await api().get('/orders-saved-views'); setViews(r.data || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadViews(); /* eslint-disable-next-line */ }, []);

  const saveCurrent = async () => {
    const name = (newViewName || '').trim();
    if (!name) return;
    try {
      await api().post('/orders-saved-views', { name, filter_json: { q, docFilter } });
      setNewViewName(''); setShowSave(false);
      await loadViews();
    } catch (e) { /* non-blocking */ }
  };
  const applyView = (v) => {
    setQ(v.filter_json?.q || '');
    setDocFilter(v.filter_json?.docFilter || '__all__');
  };
  const removeView = async (v) => {
    try { await api().delete(`/orders-saved-views/${v.id}`); await loadViews(); } catch { /* ignore */ }
  };

  // iter107 — "/" keyboard shortcut to focus the search bar
  const searchInputRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const active = document.activeElement;
      const tag = (active && active.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || (active && active.isContentEditable)) return;
      e.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        searchInputRef.current.select && searchInputRef.current.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (q.trim()) params.append('q', q.trim());
        if (docFilter && docFilter !== '__all__') params.append('doc_type', docFilter);
        const r = await api().get(`/orders-search?${params.toString()}`);
        setRows(r.data || []);
      } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [api, q, docFilter]);

  const counts = useMemo(() => {
    const c = {};
    rows.forEach(r => { c[r.badge] = (c[r.badge] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="orders-overview-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <LayoutGrid className="w-6 h-6 text-slate-700" /> Orders &amp; Shipping — Overview
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          One search across Work Orders, Sales Orders, Packing Slips, Bills of Lading, Production Orders, and Repair Orders.
        </p>
      </div>

      <Card className="industrial-card">
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-72">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
            <Input ref={searchInputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search doc #, customer, product, invoice #, PO #, RMA…    (press "/" to focus)' className="pl-8 rounded-sm" data-testid="orders-overview-search" />
          </div>
          <Select value={docFilter} onValueChange={setDocFilter}>
            <SelectTrigger className="rounded-sm w-56" data-testid="orders-overview-doc-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All document types</SelectItem>
              <SelectItem value="work_order">Work Orders (v2)</SelectItem>
              <SelectItem value="sales_order">Sales Orders</SelectItem>
              <SelectItem value="production_order">Production Orders (legacy)</SelectItem>
              <SelectItem value="packing_slip">Packing Slips</SelectItem>
              <SelectItem value="bill_of_lading">Bills of Lading</SelectItem>
              <SelectItem value="repair_order">Lyphline Repair Orders (legacy)</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 flex-wrap ml-auto">
            {Object.entries(counts).map(([k, v]) => (
              <Badge key={k} className={`${BADGE_COLORS[k] || ''} border rounded-sm text-[11px]`}>{k} · {v}</Badge>
            ))}
          </div>
        </CardContent>
        {/* iter106 — Saved views chip strip */}
        <CardContent className="px-3 pb-3 pt-0 flex items-center gap-1 flex-wrap border-t border-slate-100" data-testid="orders-overview-saved-views">
          <Bookmark className="w-3.5 h-3.5 text-slate-400 mr-1" />
          {views.length === 0 && <span className="text-xs text-slate-400 italic mr-2">No saved views yet.</span>}
          {views.map(v => (
            <Badge
              key={v.id}
              variant="outline"
              className="rounded-sm text-[11px] cursor-pointer hover:border-sky-400 hover:bg-sky-50 pr-1"
              onClick={() => applyView(v)}
              data-testid={`saved-view-${v.name}`}
            >
              {v.name}
              <button className="ml-1 text-slate-400 hover:text-red-500" onClick={(e) => { e.stopPropagation(); removeView(v); }} data-testid={`saved-view-delete-${v.name}`}><X className="w-3 h-3" /></button>
            </Badge>
          ))}
          {!showSave ? (
            <Button size="sm" variant="ghost" className="h-6 rounded-sm text-xs px-2" onClick={() => setShowSave(true)} disabled={!q.trim() && docFilter === '__all__'} data-testid="save-view-open-btn">
              <Save className="w-3 h-3 mr-1" /> Save this view
            </Button>
          ) : (
            <div className="flex items-center gap-1">
              <Input value={newViewName} onChange={(e) => setNewViewName(e.target.value)} placeholder="View name…" className="h-6 w-40 rounded-sm text-xs" autoFocus data-testid="save-view-name-input" />
              <Button size="sm" className="h-6 rounded-sm bg-slate-900 hover:bg-slate-800 text-xs px-2" onClick={saveCurrent} disabled={!newViewName.trim()} data-testid="save-view-confirm-btn">Save</Button>
              <Button size="sm" variant="ghost" className="h-6 rounded-sm text-xs px-2" onClick={() => { setShowSave(false); setNewViewName(''); }}><X className="w-3 h-3" /></Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">{rows.length} result{rows.length === 1 ? '' : 's'}</CardTitle></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Searching…</div>
          ) : !rows.length ? (
            <div className="p-6 text-sm text-slate-500 italic">No matching documents. Try a different term or clear the filter.</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Type</TableHead><TableHead>Doc #</TableHead><TableHead>Status</TableHead>
                <TableHead>Customer</TableHead><TableHead>Product / Summary</TableHead><TableHead>Created</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.doc_type}-${r.doc_id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(r.href)} data-testid={`orders-overview-row-${r.doc_number}`}>
                    <TableCell><Badge className={`${BADGE_COLORS[r.badge] || ''} border rounded-sm text-[11px]`}>{r.badge}</Badge></TableCell>
                    <TableCell className="mono font-semibold">{r.doc_number}</TableCell>
                    <TableCell><Badge variant="outline" className="rounded-sm text-[11px]">{r.status || '—'}</Badge></TableCell>
                    <TableCell className="text-sm">{r.customer_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-sm">{r.product_or_summary || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="text-xs mono">{(r.created_at || '').slice(0, 10)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersOverviewPage;
